import type { DtcCodeEntity } from '@packages/domain/dtc-codes';

import { AddDtcCode, DeleteDtcCode, UpdateDtcCode } from '@packages/domain/techline/dtc-codes';
import { container } from '@web/services/container';
import { useCallback, useState } from 'react';

// Operations are prefixed with $ to make them visually distinct in this file.
const [$addDtcCode, $deleteDtcCode, $updateDtcCode] = container.resolveOperations(
    AddDtcCode,
    DeleteDtcCode,
    UpdateDtcCode
);

/**
 * Parameters for the {@link useDtcCodes} hook.
 */
type UseTechlineDtcCodesParams = {
    /** The Techline case number that DTC codes belong to. */
    caseNumber: string;

    /** The current list of DTC codes on the case. */
    dtcCodeData: DtcCodeEntity[];

    /** Callback to update the DTC code list; accepts a new array or an updater function. */
    onDtcCodeChange: (dtcCodes: DtcCodeEntity[] | ((prev: DtcCodeEntity[]) => DtcCodeEntity[])) => void;
};

/**
 * Manages DTC code CRUD operations for a Techline case. Provides handlers for adding, deleting, changing the
 * primary code, and toggling freeze frame status. Each handler performs an optimistic UI update followed by an API
 * call, rolling back on failure.
 *
 * @param params - The case number, current DTC code data, and a callback to update the code list.
 * @returns State and handlers for DTC code management.
 */
export function useDtcCodes({ caseNumber, dtcCodeData, onDtcCodeChange }: UseTechlineDtcCodesParams) {
    // State
    const [isLoading, setIsLoading] = useState<{ [dtcCode: string]: boolean }>({});
    const [isPrimaryUpdating, setIsPrimaryUpdating] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string>('');

    // Handlers

    /**
     * Adds a DTC code to the case. Optimistically appends the code to the list (marking it primary if it's the
     * first), then confirms via API. Skips duplicates. Shows an error and rolls back on failure.
     */
    const handleAddDtc = useCallback(
        async (dtc: DtcCodeEntity[]) => {
            const selectedDtc = dtc[dtc.length - 1];
            if (!selectedDtc) {
                return;
            }

            setIsLoading((prev) => ({ ...prev, [selectedDtc.code]: true }));

            let isDuplicate = false;

            onDtcCodeChange((prev) => {
                if (prev.some((code) => code.code === selectedDtc.code)) {
                    isDuplicate = true;
                    return prev;
                }
                return [...prev, { ...selectedDtc, isPrimary: prev.length === 0 }];
            });

            if (isDuplicate) {
                return;
            }

            const params = {
                caseNumber,
                code: selectedDtc.code,
            };
            const result = await $addDtcCode(params);

            if (!result) {
                setErrorMessage('There was an error adding the DTC code. Please try again.');
                setTimeout(() => setErrorMessage(''), 5000);
                setIsLoading((prev) => ({ ...prev, [selectedDtc.code]: false }));
                return;
            }
            onDtcCodeChange([
                ...dtcCodeData,
                {
                    code: selectedDtc.code,
                    isPrimary: dtcCodeData.length === 0,
                },
            ]);
            setIsLoading((prev) => ({ ...prev, [selectedDtc.code]: false }));
        },
        [caseNumber, dtcCodeData, onDtcCodeChange]
    );

    /**
     * Removes a DTC code from the case. Optimistically removes it from the list, then confirms via API.
     * Rolls back and shows an error on failure.
     */
    const handleDeleteDtc = useCallback(
        async (selectedDtc: DtcCodeEntity) => {
            // Optimistically remove the code from the list
            onDtcCodeChange((prev) => prev.filter((selectedCode) => selectedCode.code !== selectedDtc.code));

            const params = {
                caseNumber,
                code: selectedDtc.code,
            };
            const result = await $deleteDtcCode(params);

            // Roll back on failure
            if (!result) {
                onDtcCodeChange((prev) => [...prev, selectedDtc]);
                setErrorMessage('There was an error deleting the DTC code. Please try again.');
                setTimeout(() => setErrorMessage(''), 5000);
                return;
            }
        },
        [caseNumber, onDtcCodeChange]
    );

    /**
     * Sets a DTC code as the primary code for the case. No-ops if the code is already primary or if another
     * primary change is in flight. Optimistically updates with rollback on failure.
     */
    const handlePrimaryChange = useCallback(
        async (selectedDtc: DtcCodeEntity) => {
            if (isPrimaryUpdating) {
                return;
            }

            if (selectedDtc.isPrimary) {
                return;
            }

            setIsPrimaryUpdating(true);

            const previousPrimary = dtcCodeData.find((code) => code.isPrimary);

            // Optimistically mark the selected code as primary
            onDtcCodeChange((prev) =>
                prev.map((code) => ({
                    ...code,
                    isPrimary: code.code === selectedDtc.code,
                }))
            );

            const result = await $updateDtcCode({
                caseNumber,
                code: selectedDtc.code,
                options: { isPrimary: true, freezeFrameDataInSSM: selectedDtc.hasFreezeFrameDataInSSM },
            });

            // Roll back on failure
            if (!result) {
                setErrorMessage('There was an error updating the primary DTC code. Please try again.');
                setTimeout(() => setErrorMessage(''), 5000);
                onDtcCodeChange((prev) =>
                    prev.map((code) => ({
                        ...code,
                        isPrimary: code.code === previousPrimary?.code,
                    }))
                );
            }

            setIsPrimaryUpdating(false);
        },
        [caseNumber, dtcCodeData, isPrimaryUpdating, onDtcCodeChange]
    );

    /**
     * Toggles the freeze frame data flag on a DTC code. Optimistically flips the boolean in the list,
     * then confirms via API. Rolls back on failure.
     */
    const handleToggleFreezeFrame = useCallback(
        async (selectedDtc: DtcCodeEntity) => {
            const toggled = !selectedDtc.hasFreezeFrameDataInSSM;

            // Optimistically toggle the freeze frame flag
            onDtcCodeChange((prev) =>
                prev.map((code) =>
                    code.code === selectedDtc.code ? { ...code, hasFreezeFrameDataInSSM: toggled } : code
                )
            );

            const result = await $updateDtcCode({
                caseNumber,
                code: selectedDtc.code,
                options: { freezeFrameDataInSSM: toggled },
            });

            // Roll back on failure
            if (!result) {
                setErrorMessage('There was an error updating the freeze frame status. Please try again.');
                setTimeout(() => setErrorMessage(''), 5000);
                onDtcCodeChange((prev) =>
                    prev.map((code) =>
                        code.code === selectedDtc.code
                            ? { ...code, hasFreezeFrameDataInSSM: selectedDtc.hasFreezeFrameDataInSSM }
                            : code
                    )
                );
            }
        },
        [caseNumber, onDtcCodeChange]
    );

    return {
        /** Per-code loading state keyed by DTC code string. */
        isDtcLoading: isLoading,

        /** Whether a primary code change is currently in flight. */
        isPrimaryDtcUpdating: isPrimaryUpdating,

        /** Error message from the most recent failed DTC operation; auto-clears after 5 seconds. */
        dtcErrorMessage: errorMessage,

        /** Adds the last code in the provided array to the case. */
        handleAddDtc,

        /** Removes a DTC code from the case (optimistic with rollback). */
        handleDeleteDtc,

        /** Sets a DTC code as the primary code for the case (optimistic with rollback). */
        handlePrimaryChange,

        /** Toggles the freeze frame data flag on a DTC code (optimistic with rollback). */
        handleToggleFreezeFrame,
    };
}
